import * as React from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { Card, CardContent } from '../../../../components/ui/Card';
import { Button } from '../../../../components/ui/Button';
import { Input, FormField } from '../../../../components/ui/FormField';
import { Money } from '../../../../components/ui/Money';
import { TrendingUp } from 'lucide-react';
import { DEFAULT_CURRENCY, DEFAULT_LOCALE } from '../../../../lib/locale';
import { usePNLStatement } from '../../../../features/reports/client';
import { PageHeader } from '../../../../components/layout/PageHeader';
import { Skeleton } from '../../../../components/ui/Skeleton';

export const Route = createFileRoute('/_auth/reports/financial/pnl')({
  component: ProfitLossReportPage,
});

function ProfitLossReportPage() {
  const [fromDate, setFromDate] = React.useState('2026-04-01');
  const [toDate, setToDate] = React.useState('2026-06-22');

  const { data, isLoading, isError, refetch } = usePNLStatement(fromDate, toDate);
  const revenueItems = data?.income || [];
  const expenseItems = data?.expenses || [];

  const totalRevenuePaise = revenueItems.reduce((acc, i) => acc + i.amount, 0);
  const totalExpensePaise = expenseItems.reduce((acc, i) => acc + i.amount, 0);
  const netProfitPaise = data?.netProfit || (totalRevenuePaise - totalExpensePaise);

  return (
    <div className="flex flex-col w-full h-full" style={{ viewTransitionName: 'page-body' }}>
      <PageHeader title="Profit & Loss" subtitle="Profit and loss statement for the selected period." />
      <div className="flex-1 w-full min-h-0">
        {(() => {
          if (isLoading) return <Skeleton rows={5} />;
          if (isError) return (
            <div className="p-8 text-center">
              <p className="text-sm font-semibold text-danger-600">Failed to load P&L statement</p>
              <Button variant="secondary" size="sm" className="mt-3" onClick={() => refetch()}>Retry</Button>
            </div>
          );
          return (
            <>
              <div className="bg-surface-2 p-4 rounded-xl border border-border flex flex-wrap gap-4 items-end max-w-4xl">
                <FormField label="Financial Period From">
                  <Input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} className="h-9 w-44" />
                </FormField>

                <FormField label="To Date">
                  <Input type="date" value={toDate} onChange={e => setToDate(e.target.value)} className="h-9 w-44" />
                </FormField>

                <div className="text-xs text-text-muted pb-2 pl-2">
                  Period: <span className="font-bold text-text-primary">Q1 FY 2026-27</span>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl">
                <Card className="shadow-sm">
                  <CardContent className="p-6">
                    <span className="text-caption text-text-muted font-bold uppercase tracking-wider block">Total Operating Revenue (Credit)</span>
                    <h2 className="text-2xl font-bold text-success-text mt-1">
                      <Money paise={totalRevenuePaise} currency={DEFAULT_CURRENCY} locale={DEFAULT_LOCALE} />
                    </h2>
                  </CardContent>
                </Card>

                <Card className="shadow-sm">
                  <CardContent className="p-6">
                    <span className="text-caption text-text-muted font-bold uppercase tracking-wider">Total Operating Expense (Debit)</span>
                    <h2 className="text-2xl font-bold text-danger-600 mt-1">
                      <Money paise={totalExpensePaise} currency={DEFAULT_CURRENCY} locale={DEFAULT_LOCALE} />
                    </h2>
                  </CardContent>
                </Card>

                <Card className="shadow-sm bg-accent-50/70 border-accent-200">
                  <CardContent className="p-6">
                    <span className="text-caption text-accent-700 font-bold uppercase tracking-wider block">Net Operating surplus (EBITDA)</span>
                    <h2 className="text-2xl font-bold text-accent-700 mt-1">
                      <Money paise={netProfitPaise} currency={DEFAULT_CURRENCY} locale={DEFAULT_LOCALE} />
                    </h2>
                  </CardContent>
                </Card>
              </div>
              <div className="bg-surface-1 border border-border rounded-xl shadow-sm max-w-4xl overflow-hidden">
                <div className="bg-surface-2 p-4 border-b border-border">
                  <h3 className="font-bold text-sm text-text-primary uppercase tracking-wider">Statement of Operational Income & Expenses</h3>
                </div>

                <div className="p-6 space-y-6 text-sm">
                  <div className="space-y-3">
                    <h4 className="font-bold text-xs text-text-muted uppercase tracking-wider border-b border-border pb-1">I. INCOME / REVENUE (CREDITS)</h4>
                    <div className="space-y-2">
                      {revenueItems.map((item, index) => (
                        <div key={index} className="flex justify-between py-1 border-b border-border/40 font-medium">
                          <div className="flex gap-2 text-text-secondary">
                            <span>{item.category}</span>
                          </div>
                          <span className="font-mono text-text-primary"><Money paise={item.amount} currency={DEFAULT_CURRENCY} locale={DEFAULT_LOCALE} /></span>
                        </div>
                      ))}
                      {revenueItems.length === 0 && (
                        <div className="text-text-muted py-2 italic text-center">No revenue items found.</div>
                      )}
                    </div>
                    <div className="flex justify-between py-2 font-bold text-text-primary bg-surface-2 px-3 rounded-lg border border-border/60">
                      <span>Total Revenue (A)</span>
                      <span className="font-mono text-success-text"><Money paise={totalRevenuePaise} currency={DEFAULT_CURRENCY} locale={DEFAULT_LOCALE} /></span>
                    </div>
                  </div>

                  <div className="space-y-3 pt-4">
                    <h4 className="font-bold text-xs text-text-muted uppercase tracking-wider border-b border-border pb-1">II. OPERATING EXPENSES (DEBITS)</h4>
                    <div className="space-y-2">
                      {expenseItems.map((item, index) => (
                        <div key={index} className="flex justify-between py-1 border-b border-border/40 font-medium">
                          <div className="flex gap-2 text-text-secondary">
                            <span>{item.category}</span>
                          </div>
                          <span className="font-mono text-text-primary"><Money paise={item.amount} currency={DEFAULT_CURRENCY} locale={DEFAULT_LOCALE} /></span>
                        </div>
                      ))}
                      {expenseItems.length === 0 && (
                        <div className="text-text-muted py-2 italic text-center">No expense items found.</div>
                      )}
                    </div>
                    <div className="flex justify-between py-2 font-bold text-text-primary bg-surface-2 px-3 rounded-lg border border-border/60">
                      <span>Total Expenses (B)</span>
                      <span className="font-mono text-danger-600"><Money paise={totalExpensePaise} currency={DEFAULT_CURRENCY} locale={DEFAULT_LOCALE} /></span>
                    </div>
                  </div>

                  <div className="pt-6 border-t-2 border-dashed border-border flex justify-between items-center font-bold text-base px-3">
                    <span className="text-accent-700 flex items-center gap-1.5">
                      <TrendingUp className="w-5 h-5" />
                      NET PROFIT / LOSS (A - B)
                    </span>
                    <span className="font-mono text-lg text-accent-700 bg-accent-50 px-4 py-1.5 rounded-lg border border-accent-200">
                      <Money paise={netProfitPaise} currency={DEFAULT_CURRENCY} locale={DEFAULT_LOCALE} />
                    </span>
                  </div>
                </div>
              </div>
            </>
          );
        })()}
      </div>
    </div>
  );
}
